import * as Sentry from '@sentry/browser';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { abortRefreshOnLogout } from '../../api/auth-client/refresh-singleton';
import { AllocationOfCostsCategory, ViewBy } from '../../api/financials-client/financials-client.type';
import { queryClient } from '../../query-client';
import { appConfig } from '../app-config/app-config';
import type { AuthenticationObject, AuthenticationStorage } from './storage.type';

const env = appConfig.VITE_APP_ENV_NAME;

/**
 * Resolves the `domain` for the auth cookies. The MCP OAuth flow hands the
 * `access` cookie from the FE host to the API host, which live on different
 * subdomains in every deployed env (staging.bloomfilter.app →
 * staging-api.bloomfilter.app). A host-only cookie never reaches the API, so
 * on a real bloomfilter.app host we widen the cookie to the parent domain.
 *
 * We only widen on bloomfilter.app hosts — never on localhost (dev works
 * host-only because cookies ignore the port) or on *.netlify.app preview
 * deploys, where a `.bloomfilter.app` cookie would be rejected by the browser
 * and silently break auth.
 *
 * @param {string | undefined} hostname - The current window hostname
 * @returns {string | undefined} The cookie domain, or undefined to stay host-only
 */
export function getAuthCookieDomain(hostname: string | undefined): string | undefined {
  return hostname?.endsWith('.bloomfilter.app') ? '.bloomfilter.app' : undefined;
}

const AUTH_COOKIE_PATH = '/';

/**
 * Removes every stored variant of an auth cookie — the host-only cookie written
 * before the domain migration AND the current domain-scoped cookie — so a
 * single cookie name can never resolve to two coexisting cookies.
 *
 * RFC 6265 §5.3 keys cookie identity on (name, domain, path): a host-only
 * `access` (domain = the exact host, e.g. `staging.bloomfilter.app`) and a
 * `Domain=.bloomfilter.app` `access` are DISTINCT cookies that coexist, and
 * js-cookie's `get()` returns whichever the browser lists first (§5.4: equal
 * path → earlier creation time first). A stale pre-migration cookie would thus
 * shadow the fresh value and fail the refresh-singleton read-back check
 * (refresh-singleton.ts), force-logging-out every already-authenticated user on
 * the first post-deploy refresh. `Cookies.remove` only deletes a cookie whose
 * `path`/`domain` match how it was set, so BOTH variants are removed explicitly.
 *
 * @param {string} name - The auth cookie name ('access' or 'refresh')
 * @returns {void}
 */
function removeAuthCookieAllVariants(name: string): void {
  const domain = typeof window !== 'undefined' ? getAuthCookieDomain(window.location.hostname) : undefined;
  Cookies.remove(name, { path: AUTH_COOKIE_PATH });
  if (domain) {
    Cookies.remove(name, { path: AUTH_COOKIE_PATH, domain });
  }
}

/**
 * Writes an auth cookie, first purging any pre-existing variant so a stale
 * pre-migration cookie cannot coexist with (and shadow) the fresh value.
 *
 * `access` is widened to the parent domain (`widenToDomain: true`) so the
 * top-level MCP OAuth redirect reaches the API subdomain; `refresh` stays
 * host-only — it never crosses origins (it travels only in request bodies and
 * is read only on the FE host), so widening it would needlessly expose the
 * longest-lived token to every `*.bloomfilter.app` subdomain.
 *
 * @param {string} name - The auth cookie name ('access' or 'refresh')
 * @param {string} value - The token value to store
 * @param {boolean} widenToDomain - Whether to scope the cookie to the parent domain
 * @returns {void}
 */
function setAuthCookie(name: string, value: string, widenToDomain: boolean): void {
  removeAuthCookieAllVariants(name);
  const domain =
    widenToDomain && typeof window !== 'undefined' ? getAuthCookieDomain(window.location.hostname) : undefined;
  // SameSite=Lax set explicitly (was the browser default) — sufficient for the
  // top-level GET hop to the same-site API subdomain, and removes cross-browser ambiguity.
  Cookies.set(name, value, {
    secure: env !== 'development',
    sameSite: 'lax',
    path: AUTH_COOKIE_PATH,
    ...(domain ? { domain } : {}),
  });
}

// Key to indicate logout is in progress - stored in localStorage to persist through re-renders
const LOGOUT_IN_PROGRESS_KEY = 'bloomfilter_logout_in_progress';

/**
 * Best-effort server-side blacklist of the supplied refresh token.
 *
 * Uses `fetch` with `keepalive: true` so the request is delivered to the
 * backend even though the caller is about to call `window.location.replace`,
 * which would otherwise abort an in-flight request. Failures are swallowed:
 * the backend's natural refresh-token TTL still invalidates the token on its
 * own clock, so a network blip on logout does not leave the session
 * recoverable beyond that window. Endpoint is anonymous-accessible (no
 * Authorization header needed) — the refresh token in the body is the auth.
 *
 * @param {string | undefined} refresh - The refresh token to blacklist; no-op if empty
 * @returns {void}
 */
function revokeRefreshTokenServerSide(refresh: string | undefined): void {
  if (!refresh) return;
  try {
    const url = `${appConfig.VITE_APP_API_URL}/auth/jwt/logout/`;
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
      keepalive: true,
      credentials: 'include',
    }).catch(() => {
      // Best-effort; nothing to do if the backend is unreachable.
    });
  } catch {
    // Swallow — never block logout on this.
  }
}

/**
 * Logs out the user and clears cookies, storage, and the React Query cache.
 *
 * Responsibilities:
 * 1. Set LOGOUT_IN_PROGRESS_KEY before any re-render-triggering work to prevent
 *    ProtectedRoute from capturing a stale post-login redirect path
 * 2. POST the refresh token to `/auth/jwt/logout/` so the backend blacklists it
 *    (must run BEFORE clearAuthenticationStore wipes the cookies that hold it);
 *    request uses `keepalive` so it survives the page reload below
 * 3. Clear auth cookies + Sentry/Heap identity via clearAuthenticationStore
 * 4. Wipe sessionStorage and localStorage
 * 5. Wipe in-memory React Query cache so cached server responses do not survive
 *    the brief window before reload
 * 6. Trigger a full-page reload which destroys remaining JS runtime state
 *
 * Pen-test ID 01 closure: with the backend logout endpoint live and access-token
 * TTL reduced to 15 min, the residual exposure window for a leaked access token
 * shrinks from ~12 hours to at most 15 minutes (server cannot revoke access
 * tokens; only refresh tokens are blacklisted).
 *
 * @returns {void}
 */
function logout(): void {
  // Set logout flag FIRST - this prevents ProtectedRoute from capturing redirect
  // The flag must be set before clearAuthenticationStore() which triggers re-renders.
  //
  // Guarded because this writes a NEW key: on an origin that has hit the 5 MiB
  // Web Storage quota it throws, and an unguarded throw here would abort the rest
  // of logout — including the `localStorage.clear()` below, which is exactly how a
  // quota-stuck user recovers. Losing the flag only risks capturing a stale
  // redirect path; losing the logout traps the user on a blank page.
  try {
    localStorage.setItem(LOGOUT_IN_PROGRESS_KEY, 'true');
  } catch (error) {
    console.warn('Failed to set the logout-in-progress flag; continuing with logout.', error);
  }

  // Tell the refresh singleton to drop any in-flight rotation result on the floor
  // instead of writing fresh tokens back into cookies AFTER we just cleared them.
  // Module-level state in the singleton survives microtask scheduling whereas
  // LOGOUT_IN_PROGRESS_KEY is wiped by `localStorage.clear()` further down.
  abortRefreshOnLogout();

  // Capture refresh token BEFORE clearAuthenticationStore() removes the cookie,
  // then fire the blacklist request keepalive-style so the page reload below
  // does not abort it.
  const { refresh } = getStoredAuthenticationObject();
  revokeRefreshTokenServerSide(refresh);

  clearAuthenticationStore();
  clearPostLoginRedirect();

  // Each clear is guarded independently. `Storage.clear()` throws when site data
  // is blocked, and an unguarded throw here would abort the rest of logout —
  // leaving the user signed in, on the same page, with no way out. A failed
  // sessionStorage clear must not prevent the localStorage clear, and neither
  // must prevent the navigation that actually ends the session.
  try {
    window.sessionStorage.clear();
  } catch (error) {
    console.warn('Failed to clear sessionStorage during logout; continuing.', error);
  }

  // Clear localStorage LAST and then navigate
  // The navigation will cause a page reload which resets everything
  try {
    window.localStorage.clear();
  } catch (error) {
    console.warn('Failed to clear localStorage during logout; continuing.', error);
  }

  queryClient.clear();
  window.location.replace('/');
}

/**
 * Checks if a logout is currently in progress.
 * Used to prevent capturing redirect paths during intentional sign-out.
 *
 * @returns {boolean} True if logout is in progress
 */
function isLogoutInProgress(): boolean {
  try {
    return localStorage.getItem(LOGOUT_IN_PROGRESS_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Gets the stored Authentication Object
 *
 * @returns {AuthenticationStorage} stored Authentication Object
 */
function getStoredAuthenticationObject(): AuthenticationStorage {
  const applicationStorage = {
    access: '',
    refresh: '',
  };
  applicationStorage.access = Cookies.get('access') || '';
  applicationStorage.refresh = Cookies.get('refresh') || '';

  return applicationStorage;
}

/**
 * Method to set the authentication object in the cookies
 *
 * @param {AuthenticationObject} authenticationObject a dict containing the access and refresh tokens
 * @returns {void}
 */
function storeAuthenticationObject(authenticationObject: AuthenticationObject): void {
  /* To be uncommented when we handle multiple user logging into same browser in same machine*/
  // Cookies.set("user", JSON.stringify(authenticationObject.user));
  setAuthCookie('access', authenticationObject.access || '', true);
  setAuthCookie('refresh', authenticationObject.refresh || '', false);
}

/**
 * Refreshes the stored tokens
 *
 * @param {string} access - the encoded access token
 * @param {string} refresh - the encoded refresh token
 * @returns {void}
 */
function refreshAuthenticationStore(access: string, refresh: string): void {
  setAuthCookie('access', access, true);
  setAuthCookie('refresh', refresh, false);
}

/**
 * Decodes a token and checks if it is expired
 *
 * @param {string} token - the encoded token to check
 * @returns {boolean} True if the token is expired or invalid/corrupted, false otherwise
 */
function isTokenExpired(token: string): boolean {
  try {
    const { exp } = jwtDecode(token) as { exp: number };
    return !exp || new Date(exp * 1000) < new Date();
  } catch {
    // If the token is corrupted or malformed, treat it as expired
    return true;
  }
}

/**
 * Checks if there is a valid access token stored in cookies
 *
 * @returns {boolean} True if there is a non-expired access token, false otherwise
 */
function isValidAccessToken(): boolean {
  const { access } = getStoredAuthenticationObject();

  if (!access) {
    return false;
  }

  if (isTokenExpired(access)) {
    return false;
  }

  return true;
}

/**
 * Checks whether a usable refresh token is stored — present and not expired.
 *
 * Lets the session-restore gate decide that an expired access token is
 * recoverable (silent refresh) rather than logging the user out, without
 * duplicating the token-reading/expiry logic elsewhere.
 *
 * @returns {boolean} True if a non-expired refresh token exists, false otherwise
 */
function isRefreshTokenValid(): boolean {
  const refresh = getRefreshToken();

  if (!refresh) {
    return false;
  }

  return !isTokenExpired(refresh);
}

/**
 * Clears the authentication store
 *
 * @returns {void}
 */
function clearAuthenticationStore(): void {
  // Remove BOTH the host-only (pre-migration) and domain-scoped variants of each
  // cookie, so logout can't leave a stale legacy cookie behind that would shadow
  // a later login and spiral into a logout loop. See removeAuthCookieAllVariants.
  removeAuthCookieAllVariants('access');
  removeAuthCookieAllVariants('refresh');
  Sentry.setUser(null);
  try {
    window.heap?.resetIdentity();
  } catch (error) {
    console.error('heap.resetIdentity failed during clearAuthenticationStore', error);
    Sentry.captureException(error);
  }
}

/**
 * fetches the access token from cookies
 *
 * @returns {string | undefined} the access token
 */
function getRequestToken(): string | undefined {
  return Cookies.get('access');
}

/**
 * fetches the refresh token from cookies
 *
 * @returns {string | undefined} the refresh token
 */
function getRefreshToken(): string | undefined {
  return Cookies.get('refresh');
}

/**
 * Fetches the selected subproject id from local storage
 *
 * @returns {string | null} the selected subproject id
 */
function getSelectedSubprojectId(): string | null {
  return localStorage.getItem('subproject');
}

/**
 * Deletes the selected subproject id from local storage
 *
 * @returns {void}
 */
function deleteSelectedSubprojectId(): void {
  localStorage.removeItem('subproject');
}

/**
 * Fetches the allocation of costs category from session storage
 *
 * @returns {AllocationOfCostsCategory} the allocation of costs category
 */
function getAllocationOfCostsCategory(): AllocationOfCostsCategory {
  return (
    (window.sessionStorage.getItem('allocationOfCostsCategory') as AllocationOfCostsCategory) ||
    AllocationOfCostsCategory.ASSIGNEE
  );
}

/**
 * Sets the allocation of costs category in session storage
 *
 * @param {AllocationOfCostsCategory} category - the allocation of costs category to set
 * @returns {void}
 */
function setAllocationOfCostsCategory(category: AllocationOfCostsCategory): void {
  window.sessionStorage.setItem('allocationOfCostsCategory', category);
}

/**
 * Fetches the allocation of costs unit from session storage
 *
 * @returns {ViewBy} the allocation of costs unit
 */
function getAllocationOfCostsUnit(): ViewBy {
  const fromStorage = window.sessionStorage.getItem('allocationOfCostsUnit');
  if (!fromStorage || !Object.values(ViewBy).includes(fromStorage as ViewBy)) {
    return ViewBy.TASKS;
  }
  return fromStorage as ViewBy;
}

/**
 * Sets the allocation of costs unit in session storage
 *
 * @param {ViewBy} unit - the allocation of costs unit to set
 * @returns {void}
 */
function setAllocationOfCostsUnit(unit: ViewBy): void {
  window.sessionStorage.setItem('allocationOfCostsUnit', unit);
}

/**
 * Local storage key for storing the post-login redirect path
 * Using localStorage to support cross-tab scenarios where a user might paste a deep link
 * into a new tab while their session is expired in another tab
 */
const POST_LOGIN_REDIRECT_KEY = 'bloomfilter_post_login_redirect';

/**
 * Stores the intended destination path for redirect after successful login.
 * This is used when a user attempts to access a protected route with an expired token.
 * Uses localStorage to support cross-tab scenarios.
 *
 * Note: Will not store the path if a logout is in progress (user intentionally signed out).
 *
 * @param {string} path - The full path (e.g., '/application/process-analysis')
 * @returns {void}
 */
function setPostLoginRedirect(path: string): void {
  try {
    // Don't capture redirect during intentional logout
    if (isLogoutInProgress()) {
      return;
    }
    // Only store valid paths that start with /application/
    if (path && path.startsWith('/application/')) {
      localStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
    }
  } catch (error) {
    // Fail silently if localStorage is unavailable
    console.warn('Failed to store post-login redirect path:', error);
  }
}

/**
 * Retrieves the stored post-login redirect path.
 * Returns null if no path is stored or if the stored path is invalid.
 *
 * @returns {string | null} The stored redirect path or null
 */
function getPostLoginRedirect(): string | null {
  try {
    const path = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    // Validate that the path is still a valid application route
    if (path && path.startsWith('/application/')) {
      return path;
    }
    return null;
  } catch (error) {
    // Fail silently if localStorage is unavailable
    console.warn('Failed to retrieve post-login redirect path:', error);
    return null;
  }
}

/**
 * Clears the stored post-login redirect path.
 * Should be called after successful redirect or on logout.
 *
 * @returns {void}
 */
function clearPostLoginRedirect(): void {
  try {
    localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch (error) {
    // Fail silently if localStorage is unavailable
    console.warn('Failed to clear post-login redirect path:', error);
  }
}

/**
 * Atomically retrieves and clears the stored post-login redirect path.
 * This ensures the path is retrieved and cleared in a single operation,
 * preventing race conditions where the component re-renders before navigation completes.
 *
 * @returns {string | null} The stored redirect path or null
 */
function getAndClearPostLoginRedirect(): string | null {
  try {
    const path = localStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (path && path.startsWith('/application/')) {
      // Clear immediately after retrieval
      localStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      return path;
    }
    return null;
  } catch (error) {
    console.warn('Failed to get and clear post-login redirect path:', error);
    return null;
  }
}

export {
  clearAuthenticationStore,
  clearPostLoginRedirect,
  deleteSelectedSubprojectId,
  getAllocationOfCostsCategory,
  getAllocationOfCostsUnit,
  getAndClearPostLoginRedirect,
  getPostLoginRedirect,
  getRefreshToken,
  getRequestToken,
  getSelectedSubprojectId,
  getStoredAuthenticationObject,
  isLogoutInProgress,
  isRefreshTokenValid,
  isTokenExpired,
  isValidAccessToken,
  logout,
  refreshAuthenticationStore,
  setAllocationOfCostsCategory,
  setAllocationOfCostsUnit,
  setPostLoginRedirect,
  storeAuthenticationObject,
};
